// COSTUL UNUI ELEMENT, cu rețeta lui de calcul („costurile alea trebuie să fie
// mai clare, să includă și rețeta de calcul — 0,4 kg de fier, manoperă, etc.").
//
// Socoteala e a SERVERULUI: structura măsoară elementul (betonul și cofrajul
// pe care le plătește devizul, oțelul pe familii de bare din extras), serviciul
// de planificare îl prețuiește cu prețurile unitare ale devizului
// (`control-plane/api/cost_element.py` → `cost_model.element`). Aici se cere
// și se scrie: rândurile vin ca jetoane (`viewer/cost/jetoane.ts`), cu cheile
// catalogului și cifrele, iar interfața le scrie în limba și moneda ei.

import { get } from "../api";
import { scrie, type Jeton } from "../viewer/cost/jetoane";
import { ElementIndexat } from "./elemente";

export type RandCost = {
  // „121,4 kg oțel", „8,7 ore fierari"
  cantitate: string;
  // rețeta: din ce se compune prețul
  reteta: string;
  eur: number;
  fel: "material" | "manopera";
  // cantitatea e a devizului (măsurată de pașii lui) sau derivată din
  // geometrie, fără rând propriu în deviz?
  derivat?: boolean;
};

export type CostElement = {
  randuri: RandCost[];
  totalEur: number;
  materialEur: number;
  manoperaEur: number;
  kgOtel: number | null;
  betonM3: number | null;
  cofrajM2: number | null;
  // articolele de deviz din care s-au luat prețurile unitare
  sursa: string[];
};

export type RandNivelCost = {
  ref: string;
  nivel: string;
  kgOtel: number;
  betonM3: number | null;
  cofrajM2: number | null;
  materialEur: number;
  manoperaEur: number;
  totalEur: number;
  infra: boolean;
};

export type CostStiva = {
  randuri: RandNivelCost[];
  totalEur: number;
  materialEur: number;
  manoperaEur: number;
  kgOtel: number;
  betonM3: number;
  cofrajM2: number;
  sursa: string[];
  // pozițiile de extras care n-au nimerit niciun nivel al stivei (n-ar trebui
  // să existe; dacă există, se spune, nu se pierd în tăcere)
  kgNeatribuit: number;
};

/** Costul, cum îl dă serverul: cifrele, textele ca jetoane. */
type CostElementServer = {
  randuri: { cantitate: Jeton; reteta: Jeton; eur: number; fel: "material" | "manopera"; derivat?: boolean }[];
  total_eur: number; material_eur: number; manopera_eur: number;
  kg_otel: number | null; beton_m3: number | null; cofraj_m2: number | null; sursa: string[];
};
type CostStivaServer = {
  randuri: { ref: Jeton; nivel: Jeton; kg_otel: number; beton_m3: number | null; cofraj_m2: number | null;
             material_eur: number; manopera_eur: number; total_eur: number; infra: boolean }[];
  total_eur: number; material_eur: number; manopera_eur: number; kg_otel: number; beton_m3: number; cofraj_m2: number;
  sursa: string[]; kg_neatribuit: number;
};
export type RaspunsCostElement = { masuri: unknown; cost: CostElementServer | CostStivaServer | null; note: string[] };

export function dinServerElement(c: CostElementServer): CostElement {
  return {
    randuri: c.randuri.map((r) => ({ cantitate: scrie(r.cantitate), reteta: scrie(r.reteta), eur: r.eur, fel: r.fel,
                                     ...(r.derivat ? { derivat: true } : {}) })),
    totalEur: c.total_eur, materialEur: c.material_eur, manoperaEur: c.manopera_eur,
    kgOtel: c.kg_otel, betonM3: c.beton_m3, cofrajM2: c.cofraj_m2, sursa: c.sursa,
  };
}

export function dinServerStiva(c: CostStivaServer): CostStiva {
  return {
    randuri: c.randuri.map((r) => ({ ref: scrie(r.ref), nivel: scrie(r.nivel), kgOtel: r.kg_otel, betonM3: r.beton_m3,
                                     cofrajM2: r.cofraj_m2, materialEur: r.material_eur, manoperaEur: r.manopera_eur,
                                     totalEur: r.total_eur, infra: r.infra })),
    totalEur: c.total_eur, materialEur: c.material_eur, manoperaEur: c.manopera_eur,
    kgOtel: c.kg_otel, betonM3: c.beton_m3, cofrajM2: c.cofraj_m2, sursa: c.sursa, kgNeatribuit: c.kg_neatribuit,
  };
}

const cale = (projectId: string, cloneId: string, q: string) =>
  `/api/projects/${projectId}/structure/clone/${cloneId}/cost-element?${q}`;

/** Costul elementului, cu rețeta lui. `null` = devizul nu s-a putut citi
 *  (sau elementul n-are nimic de prețuit). `cloneId` = spațiul soluției
 *  vizualizate; fără el nu e deviz, deci nici cost. */
export async function costElement(
  projectId: string,
  el: ElementIndexat,
  cloneId?: string | null
): Promise<CostElement | null> {
  if (!cloneId) return null;
  const r = await get<RaspunsCostElement>(cale(projectId, cloneId, `element=${encodeURIComponent(el.ref)}`));
  return r.cost ? dinServerElement(r.cost as CostElementServer) : null;
}

/** Costul stâlpului ÎNTREG, nivel cu nivel + mustățile din fundație
 *  („daca fac asta imi trebuie un view unde sa imi dea costul pe tot
 *  stalpul"). Nivelurile și clasa betonului le știe serverul din deviz.
 *  `null` = devizul nu s-a putut citi. */
export async function costStiva(
  projectId: string,
  stiva: string,
  cloneId?: string | null
): Promise<CostStiva | null> {
  if (!cloneId) return null;
  const r = await get<RaspunsCostElement>(cale(projectId, cloneId, `stack=${encodeURIComponent(stiva)}`));
  return r.cost ? dinServerStiva(r.cost as CostStivaServer) : null;
}
